// Audio Service für Frontend
import axios, { AxiosInstance } from 'axios';
import { AudioContent, AudioFilters, AudioFormData, PaginatedResponse, ApiPagination } from '../types/audio';
import { normalizePagination } from '../types';
import type { FassungsUebersicht } from '../types/fassungen';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';
import { getErrorData, getErrorDetails, isAxiosError, isNoResponseError } from '../utils/errors';

// Streams in der Form, in der sie ans Backend gehen (flach getippt, damit das Bauen unten ohne
// `any` auskommt). Deckt sich mit dem, was AudioController.buildStreams entgegennimmt.
type BackendAudioFile = { url: string; format: 'mp3' | 'flac' };
type BackendStream = {
  type: 'silent' | 'audio' | 'frequenz' | 'nature';
  mp3: BackendAudioFile;
  flac?: BackendAudioFile;
  volume: number;
  enabled: boolean;
};

class AudioService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = getApiBaseUrl();
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private normalizeAudioContent(content: AudioContent): AudioContent {
    if (!content) return content;
    
    // DEBUG: Logge die Content-Struktur vor Normalisierung
    console.log('🔍 normalizeAudioContent - Vor Normalisierung:', {
      id: content.id,
      title: content.title,
      hasStreams: !!content.streams,
      hasContentData: !!content.contentData,
      hasContentDataStreams: !!content.contentData?.streams,
      streamsKeys: content.streams ? Object.keys(content.streams) : [],
      contentDataStreamsKeys: content.contentData?.streams ? Object.keys(content.contentData.streams) : [],
    });
    
    // Normalisiere streams: Wenn streams nicht auf oberster Ebene, aber in contentData vorhanden
    if (!content.streams && content.contentData?.streams) {
      content.streams = content.contentData.streams;
      console.log('✅ Streams von contentData.streams zu content.streams kopiert');
    }
    
    // Stelle sicher, dass contentData.streams auch gesetzt ist (für Konsistenz)
    if (content.streams && !content.contentData) {
      content.contentData = { streams: content.streams };
    } else if (content.streams && content.contentData && !content.contentData.streams) {
      content.contentData.streams = content.streams;
    }
    
    // DEBUG: Logge die Content-Struktur nach Normalisierung
    console.log('🔍 normalizeAudioContent - Nach Normalisierung:', {
      hasStreams: !!content.streams,
      hasContentDataStreams: !!content.contentData?.streams,
      streamsKeys: content.streams ? Object.keys(content.streams) : [],
    });
    
    return content;
  }

  // Get all audio content
  async getAudioContent(filters: AudioFilters): Promise<PaginatedResponse<AudioContent>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await this.api.get<{ success: boolean; data: { content: AudioContent[]; pagination: ApiPagination } }>(
        `/api/audio?${params.toString()}`
      );
      
      // DEBUG: Logge die API-Antwort
      console.log('🔍 AudioService - API Response:', {
        success: response.data.success,
        hasData: !!response.data.data,
        hasContent: !!response.data.data?.content,
        contentLength: response.data.data?.content?.length || 0,
        firstItem: response.data.data?.content?.[0] ? {
          id: response.data.data.content[0].id,
          title: response.data.data.content[0].title,
          hasStreams: !!response.data.data.content[0].streams,
          hasContentData: !!response.data.data.content[0].contentData,
        } : null
      });
      
      if (response.data.success && response.data.data) {
        const normalized = (response.data.data.content || []).map(item => this.normalizeAudioContent(item));
        return {
          data: normalized,
          pagination: normalizePagination(response.data.data.pagination)
        };
      }

      return { data: [], pagination: normalizePagination(null) };
    } catch (error: unknown) {
      console.error('❌ Error in getAudioContent:', error);
      console.error('❌ Error details:', getErrorDetails(error));
      throw error;
    }
  }

  // Get audio content by ID
  async getAudioContentById(id: string): Promise<AudioContent> {
    const response = await this.api.get<{ success: boolean; data: AudioContent }>(`/api/audio/${id}`);
    if (response.data.success && response.data.data) {
      return this.normalizeAudioContent(response.data.data);
    }
    throw new Error('Failed to get audio content');
  }

  /**
   * Welche Fassungen dieses Werk trägt — für die Redaktion, ohne eine einzige Adresse.
   *
   * ⚠️ EIGENE Route, nicht `getAudioContentById`: die Übersicht nennt Speicherpfade und
   * Zonennamen und hängt deshalb im Backend hart an `authenticate` +
   * `authorize(['ADMIN','MODERATOR','CONTENT_CREATOR'])`, während `/api/audio/:id` auf
   * `optionalAuth` läuft (PR #741). Wer diese Abfrage je auf die öffentliche Route umbiegt, hebt
   * den offenen L0-Posten „metadata-Ausgabe filtern" still auf.
   *
   * ⛔ Es kommt hier NICHTS zum Abspielen zurück. Signierte Adressen laufen nach 12 h ab; die holt
   * das CMS bei Bedarf über `GET /api/content/:id/wiedergabe` (Übergabe §5).
   *
   * Der Fehler wird durchgereicht statt in eine leere Übersicht verwandelt: ein 403 oder 404 sähe
   * sonst aus wie „dieses Werk hat keine Fassungen" — also wie eine Aussage über die Daten, wo in
   * Wahrheit die Abfrage nicht durchkam. Die Anzeige unterscheidet das (`Fassungsliste.tsx`).
   */
  async getFassungen(id: string): Promise<FassungsUebersicht> {
    const response = await this.api.get<{ success: boolean; data: FassungsUebersicht; message?: string }>(
      `/api/audio/${id}/fassungen`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Fassungen konnten nicht geladen werden');
  }

  // Create audio content
  async createAudioContent(data: AudioFormData): Promise<AudioContent> {
    try {
      // DEBUG: Logge die originalen FormData
      console.log('🔍 Frontend - Original FormData:', JSON.stringify(data, null, 2));
      
      // Transform AudioFormData to backend format
      const transformedData = this.transformFormDataToBackendFormat(data);
      console.log('🔍 Frontend - Transformierte Daten:', JSON.stringify(transformedData, null, 2));
      
      const response = await this.api.post<{ success: boolean; data: AudioContent; message?: string }>('/api/audio', transformedData);
      
      if (response.data.success && response.data.data) {
        return this.normalizeAudioContent(response.data.data);
      }
      
      throw new Error(response.data.message || 'Failed to create audio content');
    } catch (error: unknown) {
      console.error('Error creating audio content:', error);
      if (isAxiosError(error) && error.response) {
        // Server responded with error
        const data = getErrorData(error);
        const errorMessage = data?.message || data?.error || 'Server-Fehler beim Erstellen des Audio-Inhalts';
        throw new Error(errorMessage);
      } else if (isNoResponseError(error)) {
        // Request was made but no response received
        throw new Error('Keine Antwort vom Server. Bitte überprüfen Sie Ihre Internetverbindung.');
      } else {
        // Something else happened
        throw error;
      }
    }
  }

  // Update audio content
  async updateAudioContent(id: string, data: Partial<AudioFormData>): Promise<AudioContent> {
    // Transform AudioFormData to backend format
    const transformedData = this.transformFormDataToBackendFormat(data as AudioFormData);
    
    const response = await this.api.put<{ success: boolean; data: AudioContent }>(`/api/audio/${id}`, transformedData);
    if (response.data.success && response.data.data) {
      return this.normalizeAudioContent(response.data.data);
    }
    throw new Error('Failed to update audio content');
  }

  // Delete audio content
  async deleteAudioContent(id: string): Promise<void> {
    const response = await this.api.delete<{ success: boolean; message?: string }>(`/api/audio/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete audio content');
    }
  }

  // Transform form data to backend format
  // WICHTIG: Alle Formularfelder müssen hier gesendet werden!
  private transformFormDataToBackendFormat(formData: AudioFormData): Record<string, unknown> {
    // Build streams object - auch Streams ohne URL speichern, wenn enabled gesetzt ist
    const streams: Partial<Record<'silent' | 'audio' | 'frequenz' | 'nature', BackendStream>> = {};
    
    // Silent Stream - speichern wenn URL/File vorhanden ODER wenn enabled explizit gesetzt ist
    if (formData.silentMp3Url || formData.silentMp3File || formData.silentEnabled !== undefined) {
      streams.silent = {
        type: 'silent',
        mp3: {
          url: formData.silentMp3Url || '',
          format: 'mp3'
        },
        volume: formData.silentVolume !== undefined ? formData.silentVolume : 100,
        enabled: formData.silentEnabled !== undefined ? formData.silentEnabled : false
      };
      
      // FLAC nur hinzufügen, wenn vorhanden
      if (formData.silentFlacUrl || formData.silentFlacFile) {
        streams.silent.flac = {
          url: formData.silentFlacUrl || '',
          format: 'flac'
        };
      }
    }
    
    // Audio Stream
    if (formData.audioMp3Url || formData.audioMp3File || formData.audioEnabled !== undefined) {
      streams.audio = {
        type: 'audio',
        mp3: {
          url: formData.audioMp3Url || '',
          format: 'mp3'
        },
        volume: formData.audioVolume !== undefined ? formData.audioVolume : 100,
        enabled: formData.audioEnabled !== undefined ? formData.audioEnabled : false
      };
    }
    
    // Frequenz Stream
    if (formData.frequenzMp3Url || formData.frequenzMp3File || formData.frequenzEnabled !== undefined) {
      streams.frequenz = {
        type: 'frequenz',
        mp3: {
          url: formData.frequenzMp3Url || '',
          format: 'mp3'
        },
        volume: formData.frequenzVolume !== undefined ? formData.frequenzVolume : 100,
        enabled: formData.frequenzEnabled !== undefined ? formData.frequenzEnabled : false
      };
    }
    
    // Nature Stream
    if (formData.natureMp3Url || formData.natureMp3File || formData.natureEnabled !== undefined) {
      streams.nature = {
        type: 'nature',
        mp3: {
          url: formData.natureMp3Url || '',
          format: 'mp3'
        },
        volume: formData.natureVolume !== undefined ? formData.natureVolume : 100,
        enabled: formData.natureEnabled !== undefined ? formData.natureEnabled : false
      };
    }

    // Zusätzliche Varianten-Spuren (N-pro-kind): flache UI-Liste → gruppiert { slot: [{title,mp3Url,enabled}] }.
    // Wenn das Formular Extras verwaltet (Array vorhanden): IMMER alle 4 Slots als Array senden — leerer
    // Slot bedeutet "alle Extra-Spuren dieses Slots entfernen" (Backend syncVariantExtras replace-Semantik),
    // damit sowohl Hinzufügen als auch Entfernen greift. undefined lassen, wenn das Formular Extras gar
    // nicht anfasst → Backend erhält den Bestand (mapAudioToContentRow update-preservation, kein Touch).
    let variantTracks:
      | Record<'audio' | 'frequenz' | 'nature' | 'silent', Array<{ title: string; mp3Url: string; enabled: boolean }>>
      | undefined;
    if (Array.isArray(formData.variantTracks)) {
      variantTracks = { audio: [], frequenz: [], nature: [], silent: [] };
      for (const t of formData.variantTracks) {
        if (!t || !variantTracks[t.slot]) continue;
        // Leere Zeilen (weder Datei noch Titel) überspringen — kein Geister-Track.
        if (!t.mp3Url && !t.title) continue;
        variantTracks[t.slot].push({
          title: t.title || '',
          mp3Url: t.mp3Url || '',
          enabled: t.enabled !== false,
        });
      }
    }

    // WICHTIG: Alle Formularfelder explizit senden, auch wenn leer/undefined
    // Prüfe explizit auf undefined, um zwischen "nicht gesetzt" und "leer" zu unterscheiden
    return {
      // Zusätzliche Varianten-Spuren (undefined ⇒ JSON lässt Key weg ⇒ Backend erhält Bestand).
      variantTracks,
      title: formData.title || '',
      description: formData.description !== undefined ? formData.description : '',
      // Beschreibungsfelder - explizit prüfen
      shortDescription: formData.shortDescription !== undefined ? formData.shortDescription : '',
      // ⛔ `longDescription` gibt es nicht mehr (Bereinigung 2026-09-01, CONTRACT in #769).
      //
      // ⚠️ Die Zeile fehlt hier mit Absicht und darf nicht „nachgetragen" werden: hier stand
      // `formData.longDescription !== undefined ? … : ''`. Ein nicht geführtes Feld hätte damit
      // eine leere Zeichenkette geschickt — solange die Spalte noch existierte, war das eine
      // Löschfalle; jetzt wäre es das Wiederanlegen einer dritten Wahrheit. Das Backend hält mit
      // `services/__tests__/longDescriptionAusgetragen.test.ts` dagegen.
      duration: formData.duration !== undefined ? formData.duration : '',
      headphones: formData.headphones !== undefined ? formData.headphones : 'NO',
      // Kopfhörer-Empfehlung PRO VARIANTE → metadata.headphonesByVariant (Backend buildAudioMetadata liest das).
      // Nur Varianten mit gesetztem Wert aufnehmen ('' = keine Angabe → kein Button auf der Detailseite).
      headphonesByVariant: {
        ...(formData.audioHeadphones ? { audio: formData.audioHeadphones } : {}),
        ...(formData.silentHeadphones ? { silent: formData.silentHeadphones } : {}),
        ...(formData.frequenzHeadphones ? { frequenz: formData.frequenzHeadphones } : {}),
        ...(formData.natureHeadphones ? { nature: formData.natureHeadphones } : {}),
      },
      // Affirmationen: Textarea (eine pro Zeile) → string[] (leere Zeilen raus) → metadata.affirmations.
      affirmations:
        typeof formData.affirmations === 'string'
          ? formData.affirmations.split('\n').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(formData.affirmations)
            ? formData.affirmations
            : [],
      // Frequenzen: Textarea (eine pro Zeile) → string[] → metadata.frequencies.
      frequencies:
        typeof formData.frequencies === 'string'
          ? formData.frequencies.split('\n').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(formData.frequencies)
            ? formData.frequencies
            : [],
      topicCategories: Array.isArray(formData.topicCategories) ? formData.topicCategories : [],
      productCategory: formData.productCategory !== undefined ? formData.productCategory : null,
      // Streams
      streams,
      // Metadaten - explizit prüfen, ob sie gesetzt wurden
      artist: formData.artist !== undefined ? formData.artist : '',
      album: formData.album !== undefined ? formData.album : '',
      language: formData.language !== undefined ? formData.language : 'de',
      coverImageUrl: formData.coverImageUrl !== undefined ? formData.coverImageUrl : '',
      // Relations
      artistId: formData.artistId !== undefined ? formData.artistId : null,
      // Dritter Fall derselben Bauart (nach keywords und playlistIds/productIds): die AudioForm
      // fuehrt `albumId` nirgends, also ging hier bei JEDEM Speichern ein ausdrueckliches `null`
      // raus und leerte metadata.albumId — ueber das der Album-Filter laeuft. Nur mitschicken,
      // wenn das Formular den Wert wirklich kennt.
      ...(formData.albumId !== undefined ? { albumId: formData.albumId } : {}),
      // Nur mitschicken, wenn das Formular sie wirklich fuehrt. Sonst ging hier bei JEDEM
      // Speichern eine leere Liste raus und loeschte den Bestand — dieselbe Bauart wie bei den
      // Stichwoertern: das Formular kennt die Felder nicht, der Lade-Mapper laedt sie nicht, und
      // die `: []`-Verkuerzung machte aus "nicht gefuehrt" ein "ausdruecklich leer".
      ...(Array.isArray(formData.playlistIds) ? { playlistIds: formData.playlistIds } : {}),
      ...(Array.isArray(formData.productIds) ? { productIds: formData.productIds } : {}),
      // Flags
      isPremium: Boolean(formData.isPremium),
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      // Status nur, wenn das Formular ihn führt. Hier stand `status: … || 'DRAFT'` — dieselbe Bauart, die
      // im Backend still depublizierte (`status-fehlt-faellt-auf-draft`): aus „nicht gesendet" wurde
      // „ausdrücklich Entwurf". Ein FEHLENDER Status lässt den Bestand stehen; beim Anlegen setzt das
      // Backend DRAFT selbst. (BACKLOG `cms-status-oder-draft`)
      ...(formData.status ? { status: formData.status } : {}),
      // Bündel: NUR mitschicken, wenn das Formular sie führt.
      //
      // ⚠️ Hier stand `Array.isArray(formData.audioBundles) ? formData.audioBundles : []` — und
      // weder `AudioForm` noch `mapAudioContentToFormData` kennen das Feld, `formData.audioBundles`
      // ist also IMMER undefined. Jedes Speichern schickte damit eine ausdrückliche LEERE Liste und
      // löschte `contentData.audioBundles`. Der Mapper im Backend hat dafür sogar einen Rückfall
      // auf den Bestand — der lief seit jeher ins Leere, weil hier (und in der Controller-Whitelist)
      // nie ein fehlendes Feld ankam. Genau die Bauart, die den Video-Pfad die Stichworte gekostet
      // hat. Ein FEHLENDES Feld lässt den Bestand stehen; eine gesendete leere Liste bleibt die
      // Art, die Bündel zu leeren.
      ...(formData.audioBundles !== undefined ? { audioBundles: formData.audioBundles } : {})
    };
  }
}

export const audioService = new AudioService();
